/**
 * TypeScript types — mirror the backend Pydantic schemas.
 *
 * Convention: snake_case JSON (matches the backend exactly). No camelCase
 * conversion happens on the wire; Mantine's form/input components don't care.
 */

// ─── Foundation ───────────────────────────────────────────────────────────
export type UUID = string;
export type ISODate = string;       // 'YYYY-MM-DD'
export type ISODateTime = string;   // ISO 8601

export interface ErrorResponse {
  detail: string;
  code: string | null;
}

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Maker-checker write envelope — returned by entity write hooks
 * (Phase B2 step 2). `pending=true` means the change_request was filed
 * but the live row was NOT mutated; the entity reflects the previous
 * (unchanged) state. `pending=false` means the write was auto-applied
 * (admin/compliance role) and the entity is the new state.
 *
 * `changeRequestId` is always populated (audit trail) — even auto-applied
 * writes file an APPLIED change_request row.
 */
export interface WriteResult<T> {
  entity: T;
  pending: boolean;
  changeRequestId: UUID | null;
}

// ─── Users ────────────────────────────────────────────────────────────────
export type UserRole = 'admin' | 'staff' | 'rm' | 'compliance' | 'read_only';

/** Short display label for each user role (lists, badges, filters). P41 phase 2 found the
 *  Users list showing every non-Admin as "Staff". */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  rm: 'RM',
  compliance: 'Compliance',
  read_only: 'Read-only',
  staff: 'Staff (legacy)',
};
/** P45 (Pedro 2026-10-15): the roles that READ five Admin pages — Returns, Securities,
 *  Capital Gains, produced monthly reports, Fees → History — firm-wide. Mirrors the server's
 *  ADMIN_PAGE_READ_ROLES. Non-Admins see those pages read-only (edit and export hidden). */
export const ADMIN_PAGE_READ_ROLES: UserRole[] = ['admin', 'rm', 'compliance'];
export const USER_ROLE_COLOURS: Record<UserRole, string> = {
  admin: 'orange', rm: 'blue', compliance: 'grape', read_only: 'gray', staff: 'teal',
};
export type MifirIdType = 'NCI' | 'LEI';

export interface User {
  id: UUID;
  email: string;
  display_name: string;
  role: UserRole;
  last_login_at: ISODateTime | null;
  created_at: ISODateTime;
  deleted_at: ISODateTime | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface PasswordChange {
  current_password: string;
  new_password: string;
}

/** Admin creates a new user account. */
export interface UserCreate {
  email: string;
  display_name: string;
  password: string;
  role: UserRole;
}

/** Admin updates display_name or role. Email/password have separate flows. */
export interface UserUpdate {
  display_name?: string;
  role?: UserRole;
}

/** Admin force-resets a user's password (POST /users/{id}/password-reset). */
export interface AdminPasswordReset {
  new_password: string;
}

// ─── Persons ──────────────────────────────────────────────────────────────
export type PersonKind = 'NATURAL' | 'LEGAL';
export type Gender = 'M' | 'F' | 'O';
export type IdDocType = 'CC' | 'Passaporte' | 'Outro' | 'Certificado de Residência';
export type ProfessionalStatus = 'Estudante' | 'Empregado' | 'Desempregado' | 'Reformado' | 'Outro';
export type MaritalStatus = 'Solteiro' | 'Casado' | 'União de Facto' | 'Viúvo' | 'Divorciado';
export type MaritalRegime =
  | 'Comunhão de Adquiridos' | 'Comunhão Geral' | 'Separação de Bens' | 'Outro';
export type PEPStatus =
  | 'Não' | 'PEP' | 'Membro de Família de PEP' | 'Pessoa Próxima de PEP';

/** A Person row (out shape — what GET returns). */
export interface Person {
  id: UUID;
  kind: PersonKind;
  display_name: string;
  email: string | null;
  mobile_country_code: number | null;
  mobile_num: string | null;
  fixed_phone_country_code: number | null;
  fixed_phone_num: string | null;
  pep_status: PEPStatus | null;
  us_person: boolean | null;

  // P13 (migration 0101): email-communication preferences (utils/commsPreferences).
  comms_monthly_report: boolean;
  comms_newsletter: boolean;
  comms_event_invitations: boolean;
  comms_commercial_offers: boolean;

  // Per-Person KYC dates (Phase H1.0 — two-layer KYC)
  kyc_last_date: ISODate | null;
  kyc_next_date: ISODate | null;

  // Natural-only
  gender: Gender | null;
  birth_date: ISODate | null;
  birth_place_country: string | null;
  birth_place_state: string | null;
  birth_place_district: string | null;
  birth_place_city: string | null;
  birth_place_freguesia: string | null;
  nationality_1: string | null;
  nationality_2: string | null;
  id_type: IdDocType | null;
  id_number: string | null;
  id_issuing_country: string | null;
  id_issue_date: ISODate | null;
  id_expiry_date: ISODate | null;
  fiscal_country_1: string | null;
  tin_1: string | null;
  fiscal_country_2: string | null;
  tin_2: string | null;
  fiscal_country_3: string | null;
  tin_3: string | null;
  professional_status: ProfessionalStatus | null;
  profession: string | null;
  employer: string | null;
  fiscal_address: string | null;
  fiscal_address_postal_code: string | null;
  fiscal_address_city: string | null;
  fiscal_address_state: string | null;
  fiscal_address_country: string | null;  // #69 (0070)
  mail_address: string | null;
  mail_address_postal_code: string | null;
  mail_address_city: string | null;
  mail_address_state: string | null;
  mail_address_country: string | null;  // #69 (0070)
  marital_status: MaritalStatus | null;
  marital_regime: MaritalRegime | null;
  spouse_name: string | null;
  spouse_id_type: IdDocType | null;
  spouse_id_number: string | null;
  spouse_id_expiry_date: ISODate | null;
  spouse_fiscal_country: string | null;
  spouse_tin: string | null;
  // Client-record fields, natural (P16b, migration 0105). null = not answered.
  given_names: string | null;
  surnames: string | null;
  children_count: number | null;
  children: ChildEntry[] | null;
  financial_situation: FinancialSituation | null;
  source_of_wealth: SourceOfWealth | null;
  expected_flows: ExpectedFlows | null;
  tax_obligations_met: boolean | null;

  // Legal-only
  legal_form: string | null;
  lei: string | null;
  nipc: string | null;
  incorporation_country: string | null;
  incorporation_date: ISODate | null;
  registered_office_address: string | null;
  registered_office_postal_code: string | null;
  registered_office_city: string | null;
  registered_office_state: string | null;  // #69 (0070)
  registered_office_country: string | null;
  regulatory_authority: string | null;
  // Beta #6b (2026-05-26, migration 0021): PCC + RCBE registry
  // documents. Legal-entity-only.
  pcc_code: string | null;
  pcc_expiry_date: ISODate | null;
  rcbe_code: string | null;
  rcbe_expiry_date: ISODate | null;
  // Beta #5 (migration 0023): LEI expiry.
  lei_expiry_date: ISODate | null;
  // Per-account fee/billing arc Inc 0 (migration 0057): general, editable
  // tax identifier for legal entities (non-PT entities have no TIN home —
  // only nipc/lei). Required via a completeness rule. Legal-only.
  tin: string | null;
  fiscal_country: string | null;
  // Client-record fields, legal (P16b, migration 0105).
  corporate_purpose: string | null;
  cae_code: string | null;
  listed_company: boolean | null;
  listed_market: string | null;
  high_risk_countries_declaration: string | null;
  ownership_structure: string | null;

  // Lifecycle
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
  /** #40/old-#408 (2026-06-11): roles roll-up (PARTY-ROW counts, same
   *  semantics as the detail header). Populated on LIST responses only. */
  agreement_role_count?: number | null;
  account_role_count?: number | null;
  policy_role_count?: number | null;
  /** B2-8: derived — holds a role on an ACTIVE or PROSPECT agreement (P13) or an ACTIVE account/policy.
   *  Drives the Active/Inactive status badge. LIST responses only. */
  has_active_role?: boolean | null;
  /** Pedro 2026-10-10: every agreement the person belongs to (agreement, account or policy
   *  party), "CIIF-nnn · MNEMONIC", in label order. LIST responses only. */
  agreements?: PersonAgreementRef[] | null;
}

export interface PersonAgreementRef {
  agreement_id: UUID;
  label: string;
  status: string;
}

// ─── Client-record blocks on a Person (P16b, migration 0105) ─────────────
// Mirrors backend app/schemas/person_profile.py. Amounts are euros (≥ 0).
export interface AmountLine { description: string | null; amount: number | null }
export interface ChildEntry { name: string | null; birth_date: ISODate | null }
export interface FinancialSituation {
  income: AmountLine[];
  expenses: AmountLine[];
  financial_assets: {
    deposits: number | null; securities: number | null; trusts: number | null;
    insurance: number | null; pension_funds: number | null; other: AmountLine[];
  };
  real_assets: {
    main_home: number | null; second_home: number | null; art_collections: number | null;
    other_property: AmountLine[]; other: AmountLine[];
  };
  liabilities: { short_term_loans: number | null; mortgages: number | null; other: AmountLine[] };
}
export interface Inheritance {
  amount: number | null; date_received: ISODate | null; donor: string | null; donor_source: string | null;
}
export interface SourceOfWealth {
  professional_activity: number | null;
  financial_investments: number | null;
  business_sales: AmountLine[];
  inheritances: Inheritance[];
  real_estate: AmountLine[];
  other: AmountLine[];
}
export interface ExpectedFlows { inflows: AmountLine[]; outflows: AmountLine[] }

/** PATCH/POST input for /persons. All fields optional except those required by the backend. */
export type PersonInput = Partial<Omit<Person, 'id' | 'created_at' | 'updated_at' | 'deleted_at' | 'created_by' | 'updated_by'>>;

// ─── Agreements ───────────────────────────────────────────────────────────
export type ServiceType = 'CIIF' | 'CEC';
export type ClientType = 'Particular' | 'Empresa';
/** Shared lifecycle status across Agreement, Account, InsurancePolicy.
 *  Introduced by backend migration 0028 (2026-06-05) replacing the prior
 *  per-table Activo/Inactivo enum on Agreement. */
// #51 (2026-06-14): Prospect = a pre-conversion lead (Agreement-only). Shared
// type, but only Agreement ever carries it; a Prospect has no client_number
// until it is promoted to Active.
export type LifecycleStatus = 'Active' | 'Inactive' | 'Closed' | 'Prospect';

// #322 (2026-06-08): reason a Policy reached the terminal Closed state.
// Stamped by the redeem / lapse / mature termination endpoints on
// transition to Closed. NULL otherwise. Enforced by
// insurance_policies_close_reason_chk: status='Closed' ⇔ NOT NULL.
// #29 (2026-06-11): AGREEMENT_CLOSED is system-stamped by the
// Agreement→Closed cascade — never user-selectable.
export type CloseReason = 'REDEEMED' | 'LAPSED' | 'MATURED' | 'AGREEMENT_CLOSED';

// The reasons a USER can choose in the terminate flow. AGREEMENT_CLOSED
// is excluded — only the backend cascade stamps it.
export type UserCloseReason = Exclude<CloseReason, 'AGREEMENT_CLOSED'>;

// §3j friendly labels for close_reason wherever a human reads it
// (badges, previews). Raw enum values are for payloads only.
export const CLOSE_REASON_LABELS: Record<CloseReason, string> = {
  REDEEMED: 'Redeemed',
  LAPSED: 'Lapsed',
  MATURED: 'Matured',
  AGREEMENT_CLOSED: 'Agreement closed',
};

// Lifecycle redesign Phase 1 (C-166): agreement-level Inactive reason (distinct
// from the policy-level CloseReason above; 'Closed' is retired for agreements).
// SUSPENDED = mandate paused (default for a generic inactivation); EX_CLIENT =
// an Active client relationship that ended; LOST_LEAD = a Prospect that never
// converted.
export type AgreementInactiveReason = 'SUSPENDED' | 'EX_CLIENT' | 'LOST_LEAD';

// §3j friendly labels — single source for badges/summary wherever a human reads it.
export const AGREEMENT_INACTIVE_REASON_LABELS: Record<AgreementInactiveReason, string> = {
  SUSPENDED: 'Suspended',
  EX_CLIENT: 'Ex-client',
  LOST_LEAD: 'Lost lead',
};

// #104 Wave B (2026-06-08): move-on-attach types.
export interface MoveIndirectAccountItem {
  id: UUID;
  label: string;
}
export interface MoveFootprint {
  policy_id: UUID;
  policy_label: string;
  source_agreement_id: UUID | null;
  source_agreement_label: string | null;
  target_agreement_id: UUID;
  target_agreement_label: string;
  policy_current_status: LifecycleStatus;
  indirect_accounts: MoveIndirectAccountItem[];
  summary: Record<string, unknown>;
}
export interface MoveRequest {
  /** The plan_dict returned by the preview endpoint, passed back
   *  unchanged. Used at apply-time for mismatch detection. */
  move_plan: Record<string, unknown>;
  justification?: string | null;
}
export interface MoveResponse {
  policy_id: UUID;
  new_agreement_id: UUID | null;
  previous_agreement_id: UUID | null;
  affected_indirect_account_count: number;
  change_request_id: UUID | null;
  change_request_status: 'applied' | 'pending' | null;
}

/** @deprecated 2026-06-08 (#273) — use LifecycleStatus. Kept as an alias
 *  only to avoid breaking any remaining consumer in transit; will be
 *  removed once all references are migrated. */
export type AgreementStatus = LifecycleStatus;
export type MifidInvestorNature = 'NP' | 'P' | 'CE';
export type MifidResidence = 'R' | 'NR';
/** #50 stage 2 (migration 0036): language of generated client documents. */
export type DocumentLanguage = 'PT' | 'EN';
export type StrategicAllocation =
  // #52 (migration 0037): solutions more conservative than SAA00.
  | 'CASH' | 'CASH_PLUS' | 'STB'
  | 'SAA00'
  | 'SAA10' | 'SAA20' | 'SAA30' | 'SAA40' | 'SAA50'
  | 'SAA60' | 'SAA70' | 'SAA80' | 'SAA90' | 'SAA100';

/** #52: the ESG axis (SFDR Art.8/Art.9 preference + minimum-share band). */
export type EsgScore =
  | 'ESG_0'
  | 'ESG_8_0_10' | 'ESG_8_10_25' | 'ESG_8_25_50' | 'ESG_8_50_100'
  | 'ESG_9_0_10' | 'ESG_9_10_25' | 'ESG_9_25_50' | 'ESG_9_50_100';
export type AmlRisk = 'Normal' | 'Elevado';

/** Compact reference rendered in AgreementOut.named_rms for the list
 *  page Named RM column (#276 polish, 2026-06-08). Server-set via a
 *  batched single-JOIN query; matches backend NamedRMRef schema. */
export interface NamedRMRef {
  user_id: UUID;
  display_name: string;
  is_primary: boolean;
}

// ─── Agreement Users (Named RM assignments) — #276 (2026-06-08) ──────────
// Pure metadata: does NOT affect permission scope. Compliance / Admin
// remain the only roles able to write to agreement_users; reads are open
// to any authenticated user. Backend was shipped under #271.
export interface AgreementUser {
  id: UUID;
  agreement_id: UUID;
  user_id: UUID;
  is_primary: boolean;
  /** Server-stamped at create. */
  assigned_at: string;
  /** Server-stamped at create — the Compliance/Admin user who made
   *  the assignment, NOT the User being assigned. */
  assigned_by_user_id: UUID;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  /** #56 (2026-06-13, C-134 class, §3j): server-resolved label for the
   *  ASSIGNED user (the Named RM) — display_name, email fallback. Lets
   *  staff/RM viewers render names without the admin-gated GET /users.
   *  null when unresolved. */
  user_display_name?: string | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AgreementUserCreateInput {
  agreement_id: UUID;
  user_id: UUID;
  is_primary?: boolean;
}

export interface AgreementUserUpdateInput {
  /** Only field that can be patched post-create. To change agreement_id
   *  or user_id, revoke (soft-delete) and create a new row. */
  is_primary: boolean;
}

export interface Agreement {
  id: UUID;
  service_type: ServiceType;
  client_number: number;
  code_1: string;        // GENERATED — read-only
  client_mnemonic: string;
  client_type: ClientType;
  status: LifecycleStatus;
  /** #50 stage 2: default language for generated documents (Export-New modal). */
  preferred_language: DocumentLanguage;
  /** MR-C (0126): the Portfolios tab's Ref Ccy preset and the Monthly Report's portfolio pages. */
  preferred_ref_currency: string;

  mifid_investor_nature: MifidInvestorNature | null;
  mifid_residence: MifidResidence | null;
  mifir_id: string | null;
  mifir_id_type: MifirIdType | null;
  mifid_suitability_score: number | string | null;   // Decimal arrives as string from PG
  mifid_suitability_last_date: ISODate | null;
  mifid_suitability_next_date: ISODate | null;       // user-editable; clamped server-side to last_date + rule_interval
  mifid_suitability_review_is_manual_override: boolean;  // server-set: next_date < rule ceiling
  strategic_allocation: StrategicAllocation | null;
  /** #52: ESG axis between Score and Allocation; AQ-populated, adviser-editable. */
  esg_score: EsgScore | null;

  // KYC (Phase D2) — agreement-level review umbrella
  kyc_last_date: ISODate | null;
  kyc_next_date: ISODate | null;                     // user-editable; clamped server-side
  kyc_review_is_manual_override: boolean;            // server-set: next_date < rule ceiling

  // D2-polish 3: derived from agreement_documents (server-set; for list & summary bar)
  iaa_initial_date: ISODate | null;
  close_date: ISODate | null;
  // Lifecycle redesign Phase 1: why an agreement is Inactive. NULL unless
  // status === 'Inactive'. Stamped by the cascade flow (apply_cascade); never
  // user-editable. SUSPENDED (default) / EX_CLIENT (ended client) / LOST_LEAD
  // (unconverted Prospect).
  inactive_reason: AgreementInactiveReason | null;

  // Beta v3 polish B (2026-05-27): worst-case rollup across parties.
  // Populated by GET /agreements (list); GET /agreements/{id} leaves both
  // null because the detail page does its own client-side rollup.
  //   pep: 'PEP' | 'Related' | 'Nao' | null  (null = at least one party unanswered or no parties)
  //   us:  true (any Yes) | false (all No) | null  (any unanswered or no parties)
  aggregated_pep_status: 'PEP' | 'Related' | 'Nao' | null;
  aggregated_us_person: boolean | null;

  // #276 polish (2026-06-08): Named RM batched join, populated by the
  // list endpoint only (detail-page chip uses useAgreementUsers
  // directly). Empty array when no assignments. Primary tier first.
  named_rms: NamedRMRef[];

  aml_risk: AmlRisk | null;
  aml_last_date: ISODate | null;
  aml_next_date: ISODate | null;

  // Beta #3 (2026-05-26, migration 0022): KYC contact snapshot.
  // Frozen-in-time copy of a Person's contact details at the moment
  // the user picked them in the KYC-contact picker on the KYC tab.
  kyc_contact_person_id: UUID | null;
  kyc_contact_email: string | null;
  kyc_contact_phone_country_code: number | null;
  kyc_contact_phone_num: string | null;
  kyc_contact_address: string | null;                     // user-editable; clamped server-side to last_date + rule_interval
  // Team feedback P15 (migration 0099): how the client came to Baluarte.
  client_origin: ClientOrigin | null;
  client_origin_referrer_id: UUID | null;                 // only with 'Referred by'
  client_origin_other: string | null;                     // only with 'Other'
  aml_review_is_manual_override: boolean;            // server-set: next_date < rule ceiling

  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AgreementCreateInput {
  service_type: ServiceType;
  // #51: omitted for a Prospect (no number until promotion); required by the
  // backend for any non-Prospect status.
  client_number?: number | null;
  client_mnemonic: string;
  client_type: ClientType;
  status?: LifecycleStatus;
  preferred_language?: DocumentLanguage;
  preferred_ref_currency?: string;
  mifid_investor_nature?: MifidInvestorNature | null;
  mifid_residence?: MifidResidence | null;
  mifir_id?: string | null;
  mifir_id_type?: MifirIdType | null;
  mifid_suitability_score?: number | string | null;
  mifid_suitability_last_date?: ISODate | null;
  mifid_suitability_next_date?: ISODate | null;       // user-editable; backend clamps if > rule ceiling
  strategic_allocation?: StrategicAllocation | null;
  esg_score?: EsgScore | null;
  kyc_last_date?: ISODate | null;
  kyc_next_date?: ISODate | null;                     // user-editable; backend clamps if > rule ceiling
  aml_risk?: AmlRisk | null;
  aml_last_date?: ISODate | null;
  aml_next_date?: ISODate | null;                     // user-editable; backend clamps if > rule ceiling
  client_origin?: ClientOrigin | null;
  client_origin_referrer_id?: UUID | null;
  client_origin_other?: string | null;
}

/** Team feedback P15 (migration 0099). Stored as the human label (like ClientType). */
export type ClientOrigin = 'RM' | 'Referred by' | 'Website' | 'Media' | 'Other';

// ─── Compliance rules (Phase D1) ──────────────────────────────────────────
export interface ComplianceRule {
  id: UUID;
  rule_key: string;
  applies_when: Record<string, string>;
  interval_months: number;
  description: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
}

export interface ComplianceRuleCreateInput {
  rule_key: string;
  applies_when?: Record<string, string>;
  interval_months: number;
  description?: string | null;
}

export interface ComplianceRuleUpdateInput {
  interval_months?: number;
  description?: string | null;
}

export interface RecomputeReport {
  rule: ComplianceRule;
  recomputed: { agreements?: number };
}

export type AgreementUpdateInput = Omit<
  AgreementCreateInput,
  'service_type' | 'client_number' | 'client_mnemonic' | 'client_type'
>;

// ─── Translation registry (#50 stage 2, migration 0036) ──────────────────
export type TranslationTermDomain =
  | 'CLASSIFICATION'
  | 'MARITAL_STATUS'
  | 'MARITAL_REGIME'
  | 'ID_DOCUMENT_TYPE'
  | 'COUNTRY';

export interface TranslationTerm {
  id: UUID;
  domain: TranslationTermDomain;
  db_value: string;
  language: DocumentLanguage;
  term: string;
  notes: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  pending_change_requests: PendingChangeRequestRef[];
}

export interface TranslationTermCreateInput {
  domain: TranslationTermDomain;
  db_value: string;
  language: DocumentLanguage;
  term: string;
  notes?: string | null;
}

export interface TranslationTermUpdateInput {
  term?: string;
  notes?: string | null;
}

// ─── Adequacy questionnaire (#52, migration 0037) ─────────────────────────
export interface AqSubmission {
  answers: Record<string, unknown>;
  financials: Record<string, unknown>;
  notes?: string | null;
  /** #52 polish 2: allocation staged at save time (snapshotted per version;
   *  the exceeds flag computes server-side). */
  allocation_chosen?: StrategicAllocation | null;
}

export interface AqPreview {
  complete: boolean;
  missing: string[];
  section_scores: Record<string, number>;
  profile_score: number | null;
  esg_derived: EsgScore | null;
  checks: string[];
  derived_financials: Record<string, number>;
}

export interface AqResponse {
  id: UUID;
  agreement_id: UUID;
  answers: Record<string, unknown>;
  financials: Record<string, unknown>;
  section_scores: Record<string, number>;
  profile_score: number | null;
  esg_derived: EsgScore | null;
  checks: string[];
  notes: string | null;
  /** #52 polish 2 (migration 0038): per-version snapshots. */
  allocation_chosen: StrategicAllocation | null;
  allocation_exceeds: boolean | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  pending_change_requests: PendingChangeRequestRef[];
}

export interface SuitabilityConversionRow {
  id: UUID;
  score_from: number;
  score_to: number;
  allocations: StrategicAllocation[];
  notes: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  pending_change_requests: PendingChangeRequestRef[];
}

// ─── Accounts ─────────────────────────────────────────────────────────────
export interface Account {
  id: UUID;
  // Migration 0028 (2026-06-05): agreement_id relaxed to nullable. NULL for
  // Indirect Accounts (owned via opened_by_policy_id) and orphan/limbo Accounts.
  // Direct Accounts have agreement_id populated and opened_by_policy_id NULL.
  agreement_id: UUID | null;
  // Migration 0028: NEW — when populated, this Account is Indirect (owned by
  // the referenced Policy). Mutually exclusive with agreement_id by schema CHECK.
  opened_by_policy_id: UUID | null;
  // Migration 0028: NEW — server-resolved Agreement context. For Direct Accounts
  // equals agreement_id; for Indirect equals policy.agreement_id (may be null
  // if the owning Policy is in Inactive limbo).
  effective_agreement_id: UUID | null;
  status: LifecycleStatus;
  rto_approved: boolean;
  /** The account's bank (Banks list, migration 0112). While set, `bank_name` and
   *  `bank_mnemonic` are the bank's (kept in step by the database); the bank's
   *  address, SWIFT/BIC and contacts are read from the bank (`banksApi.get`). */
  bank_id: UUID | null;
  bank_name: string;
  // Nullable since backend migration 0019 (real-data import relaxation).
  internal_num: string | null;
  iban: string | null;
  // The fee-invoice import's matchers (migration 0066; C-229): the bank's mnemonic
  // (direct accounts) and the fund number (indirect accounts).
  bank_mnemonic: string | null;
  fund_number: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AccountInput {
  agreement_id?: UUID;
  /** Phase A2 (2026-06-09): create-time alternative to agreement_id for
   *  Indirect accounts (account belongs to a Policy, not directly to an
   *  Agreement). The backend AccountCreate schema validates: exactly one
   *  of agreement_id / opened_by_policy_id must be set at create time. */
  opened_by_policy_id?: UUID;
  /** The bank, from the Banks list (0112): the server fills its name and mnemonic. */
  bank_id?: UUID | null;
  internal_num?: string;
  rto_approved?: boolean;
  iban?: string | null;
  fund_number?: string | null;
}

// ─── Agreement parties ────────────────────────────────────────────────────
export type PartyRole = 'HOLDER' | 'REPRESENTATIVE' | 'BENEFICIARY';
export type BeneficiarySubtype = 'LEGAL_HEIR' | 'WILL_HEIR';
export type RepresentativePower = 'LPOA' | 'MANDATO';

export interface AgreementParty {
  id: UUID;
  agreement_id: UUID;
  person_id: UUID;
  role: PartyRole;
  related_party_id: UUID | null;
  is_ubo: boolean;
  beneficiary_subtype: BeneficiarySubtype | null;
  representative_power_type: RepresentativePower | null;
  power_of_attorney_reference: string | null;
  notes: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  // Server-resolved person fields. Added 2026-05-28 (Task #231) so pages
  // consuming AgreementParty lists don't have to fan out one
  // /api/persons/<id> request per party row.
  //
  // person_display_name: the Person's display_name. Will be null only if
  // the referenced Person has been hard-deleted (defensive — should not
  // happen under normal FK constraints).
  //
  // person_pep_status / person_us_person feed the client-side PEP and
  // FATCA aggregation rollups in AgreementDetailPage. The full aggregate
  // is also available server-side on the Agreement object itself as
  // aggregated_pep_status / aggregated_us_person — these per-party fields
  // power the breakdown tooltip ("Maria: PEP / João: Não / ...").
  person_display_name: string | null;
  person_pep_status: string | null;
  person_us_person: boolean | null;
  /** #50 stage 2: 'NATURAL' | 'LEGAL' — Export-IAA modal's Position field
   *  shows only for LEGAL holders. */
  person_kind: string | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AgreementPartyCreateInput {
  agreement_id: UUID;
  person_id: UUID;
  role: PartyRole;
  related_party_id?: UUID | null;
  is_ubo?: boolean;
  beneficiary_subtype?: BeneficiarySubtype | null;
  representative_power_type?: RepresentativePower | null;
  power_of_attorney_reference?: string | null;
  notes?: string | null;
}

// Pedro 2026-05-04 round-6: role is now mutable on PATCH (was previously
// excluded). agreement_id and person_id remain immutable — changing
// those means a different relationship, soft-delete + re-create.
export type AgreementPartyUpdateInput = Omit<AgreementPartyCreateInput, 'agreement_id' | 'person_id'>;

// ─── Account parties ──────────────────────────────────────────────────────
export type AccountPartyRole = 'JOINT_HOLDER' | 'INSURANCE_BENEFICIARY';

export interface AccountParty {
  id: UUID;
  account_id: UUID;
  person_id: UUID;
  role: AccountPartyRole;
  notes: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  // Task #239 (2026-06-02): server-resolved person display name to drop
  // the per-row PersonRef cascade from Account-side tables (account
  // detail Parties tab + standalone /account-parties list page).
  // null only when the Person row has been hard-deleted.
  person_display_name: string | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AccountPartyCreateInput {
  account_id: UUID;
  person_id: UUID;
  role: AccountPartyRole;
  notes?: string | null;
}

export type AccountPartyUpdateInput = Omit<AccountPartyCreateInput, 'account_id' | 'person_id' | 'role'>;

// ─── Account insurance ────────────────────────────────────────────────────
// Phase H1.6: account_id (singular) replaced by account_ids (plural). A
// policy now has a many-to-many relationship with accounts via the
// insurance_policy_accounts join table. The Out form materialises
// account_ids from the join at serialize time.
export interface AccountInsurance {
  id: UUID;
  // Migration 0028 (2026-06-05): agreement_id re-introduced as the single
  // source of truth for the Policy's Agreement context. NULL when Inactive
  // or Closed (per CHECK constraint: Active ⇔ agreement_id IS NOT NULL).
  agreement_id: UUID | null;
  status: LifecycleStatus;
  // #322 (2026-06-08): metadata on the Closed state. NULL unless
  // status === 'Closed'. Stamped by the redeem / lapse / mature
  // termination endpoints; never editable via PATCH.
  close_reason: CloseReason | null;
  // Derived list of Indirect Accounts (server-resolved from
  // accounts WHERE opened_by_policy_id = policy.id).
  account_ids: UUID[];
  insurer: string;
  policy_number: string;
  fund_type: string | null;
  fund_number: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_fax: string | null;
  contact_email: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AccountInsuranceInput {
  // #274 Wave A.2 (2026-06-08): agreement_id is REQUIRED at create time
  // (the backend AccountInsuranceCreate schema enforces this — a Policy
  // must belong to an Agreement to satisfy the
  // insurance_policies_status_agreement_chk constraint). Optional in
  // this type because the same shape is also used for updates, where
  // agreement_id is immutable and must NOT be sent.
  agreement_id?: UUID;
  // On create: list of account_ids to attach (zero allowed; completeness
  // rule _has_account flags an empty policy). The backend validates
  // these are orphan-eligible (BOTH FKs NULL, status=Inactive) and
  // atomically promotes them to Indirect on the new Policy via the
  // _post_apply_orphan_account_ids side-channel under the same
  // Compliance approval. On update: NOT updatable here — use
  // /insurance-policy-accounts to add or remove individual joins
  // (each is a regulated event with its own audit trail).
  account_ids?: UUID[];
  insurer?: string;
  policy_number?: string;
  fund_type?: string | null;
  fund_number?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_fax?: string | null;
  contact_email?: string | null;
}

// ─── Insurance policy accounts ────────────────────────────────────────────
// The InsurancePolicyAccount + InsurancePolicyAccountInput types (legacy
// M:N join row + create payload) were removed in the #388 cleanup
// (2026-06-09). Migration 0028 collapsed the join into a single FK
// (`Account.opened_by_policy_id`); the only surviving operation — attach
// an orphan Account to a Policy — now lives at
// `accountInsuranceApi.attachAccount(policy_id, account_id)` and returns
// `AccountInsurance` (the refreshed Policy), not a join row.

// ─── Audit log ────────────────────────────────────────────────────────────
export type AuditAction = 'INSERT' | 'UPDATE' | 'SOFT_DELETE' | 'RESTORE' | 'HARD_DELETE';
export type AuditSource = 'UI' | 'API' | 'MIGRATION' | 'SYSTEM';

export interface AuditLogEntry {
  id: number;
  ts: ISODateTime;
  actor_user_id: UUID | null;
  actor_label: string | null;
  // Pedro 2026-05-04 round-5 fixup: backend resolves users.email at
  // read-time when actor_label is null (typical for trigger-driven
  // rows). UI fallback chain: actor_label → actor_email_resolved →
  // 8-char UUID prefix → em-dash.
  actor_email_resolved: string | null;
  entity_type: string;
  entity_id: string;
  // Pedro 2026-05-04 round-3: human-friendly identifier resolved at
  // read-time by the backend (e.g. user→email, agreement→code_1,
  // person→display_name, account→bank+last4 of IBAN). Null when no
  // friendly label can be derived (orphaned row, unknown entity_type).
  // Frontend renders this primarily and falls back to entity_id.
  friendly_label: string | null;
  action: AuditAction;
  source: AuditSource;
  old_state: Record<string, unknown> | null;
  new_state: Record<string, unknown> | null;
  diff_keys: string[] | null;
  notes: string | null;
  /** AUD-JR: a replace-style line's HARD_DELETE / INSERT explained (null otherwise). */
  replace_note?: string | null;
}

// ─── Insurance Policy Parties (Phase H1.5) ────────────────────────────────
// Generalises the prior insurance_beneficiaries surface into a single table
// with a role discriminator covering all three policy-level role types.
// BENEFICIARY rows additionally carry designation / percentage_share /
// designation_date; POLICY_HOLDER and LIFE_INSURED rows have those NULL
// (enforced by DB CHECK constraint ipp_non_beneficiary_no_designation).
export type InsurancePolicyPartyRole = 'POLICY_HOLDER' | 'LIFE_INSURED' | 'BENEFICIARY';

export type BeneficiaryDesignation = 'PRIMARY' | 'CONTINGENT';

export interface InsurancePolicyParty {
  id: UUID;
  policy_id: UUID;
  // Pedro 2026-05-04 round-5: human-friendly label for the policy,
  // resolved at read-time by the backend ('Generali · POL-99812').
  // Null only when both insurer and policy_number are null on the
  // policy row (extremely rare). Frontend prefers this over policy_id
  // for any UI that displays the policy to a human.
  policy_label: string | null;
  person_id: UUID;
  role: InsurancePolicyPartyRole;
  designation: BeneficiaryDesignation | null;
  percentage_share: number | null;
  designation_date: ISODate | null;
  notes: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  approval_status: string;
  approved_at: ISODateTime | null;
  approved_by: UUID | null;
  approved_via_change_request_id: UUID | null;
  // Server-resolved person display name (Task #231, 2026-05-28). Lets
  // any list-table render the person's name without firing a per-row
  // /api/persons/<id> follow-up GET.
  person_display_name?: string | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface InsurancePolicyPartyInput {
  policy_id?: UUID;
  person_id?: UUID;
  role?: InsurancePolicyPartyRole;
  designation?: BeneficiaryDesignation | null;
  percentage_share?: number | null;
  designation_date?: ISODate | null;
  notes?: string | null;
}

// ─── Agreement Documents (Phase D2) ───────────────────────────────────────
// Dynamic IAA history: each row is an event (initial signing, amendment,
// closure). Replaces the fixed signature_date / amendment_1-4_date /
// close_date columns.
export type AgreementDocumentKind = 'IAA_INITIAL' | 'AMENDMENT' | 'CLOSE';

export interface AgreementDocument {
  id: UUID;
  agreement_id: UUID;
  kind: AgreementDocumentKind;
  ordinal: number;
  label: string;
  signed_date: ISODate | null;
  document_ref: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AgreementDocumentCreateInput {
  agreement_id: UUID;
  kind?: AgreementDocumentKind;
  ordinal?: number;
  label: string;
  signed_date?: ISODate | null;
  document_ref?: string | null;
}

// Update cannot change kind (DB-enforced uniqueness invariant on
// IAA_INITIAL / CLOSE).
export interface AgreementDocumentUpdateInput {
  label?: string;
  signed_date?: ISODate | null;
  document_ref?: string | null;
  ordinal?: number;
}

// Change Requests
export type ChangeRequestKind = 'CREATE' | 'UPDATE' | 'DELETE' | 'RESTORE';
export type ChangeRequestStatus = 'PENDING' | 'APPLIED' | 'REJECTED' | 'CANCELLED';


// Task 4 (2026-06-09): post-Save CR visibility ref shape — mirrors backend
// app.schemas.change_requests.ChangeRequestRef. Returned per-row on every
// maker-checker-aware entity's Out schema as `pending_change_requests`.
// Empty array when no pending CRs against this entity row.
export interface PendingChangeRequestRef {
  id: UUID;
  kind: ChangeRequestKind;
  requested_by: UUID;
  /** Populated by the backend via LEFT JOIN users. Null if user missing. */
  requested_by_name: string | null;
  requested_at: ISODateTime;
  /** #26 polish (2026-06-10): server-derived nature of the operation
   *  ("Detach", "Terminate · LAPSED", "Attach to Agreement", "Update", …)
   *  so badges can say WHAT is pending. */
  op_label?: string | null;
  /** #37/old-#347 (2026-06-11): the field names an UPDATE CR changes
   *  (payload keys minus plan/side-channel keys). Drives the
   *  field-overlap warning before a maker files a competing change.
   *  Null/absent for non-UPDATE kinds. */
  changed_fields?: string[] | null;
}

/** Phase 5 (C-160 / lifecycle-redesign invariant 6): server-derived
 *  "how impactful is this CR" summary so the checker UI renders a
 *  high-impact banner + approve-confirm gate and the CR lists a badge —
 *  from one source of truth. Mirrors backend
 *  app.schemas.change_requests.ImpactSummary. */
export interface ImpactSummary {
  is_high_impact: boolean;
  /** Child records the cascade will touch; 0 for a Promote. */
  affected_total: number;
  /** Friendly operation label ("Promote to Active", "Detach", …). */
  op_label?: string | null;
}

export interface ChangeRequest {
  id: UUID;
  kind: ChangeRequestKind;
  entity_type: string;
  entity_id: UUID | null;
  /** #49 (2026-06-13, C-134, §3j): server-resolved friendly label of the
   *  TARGET entity. Resolved without a deleted_at filter so applied-DELETE
   *  CRs (soft-deleted target) still get a human name. Null/absent for
   *  unapplied CREATE or an unresolvable target — pass `?? undefined` to
   *  EntityFriendlyLabel's preResolvedLabel so it falls back to its legacy
   *  client-side path when null. */
  entity_friendly_label?: string | null;
  payload: Record<string, unknown>;
  justification: string | null;
  status: ChangeRequestStatus;
  requested_by: UUID;
  requested_by_name: string | null;
  requested_at: ISODateTime;
  decided_by: UUID | null;
  decided_by_name: string | null;
  decided_at: ISODateTime | null;
  decision_notes: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  /** Phase 5 (C-160): server-derived high-impact summary (drives the
   *  banner/badge/approve-gate). Absent on older payloads; null on
   *  endpoints that don't attach it. */
  impact?: ImpactSummary | null;
}

export interface ChangeRequestDecision {
  decision_notes?: string;
}

// ─── Completeness rules (Phase E1) ────────────────────────────────────────
export type CompletenessEntityType = 'AGREEMENT' | 'PERSON' | 'ACCOUNT' | 'INSURANCE_POLICY';

export interface CompletenessRule {
  id: UUID;
  entity_type: CompletenessEntityType;
  applies_when: Record<string, string>;
  field_name: string;
  description: string | null;
  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface CompletenessRuleCreateInput {
  entity_type: CompletenessEntityType;
  applies_when?: Record<string, string>;
  field_name: string;
  description?: string | null;
}

export interface CompletenessRuleUpdateInput {
  description?: string | null;
}

export interface IncompleteRecord {
  entity_type: CompletenessEntityType;
  entity_id: UUID;
  label: string;
  missing_fields: string[];
  /** Parallel to missing_fields; rule.description for each missing field. */
  explanations: string[];
  detail_path: string;
}

export interface IncompleteReport {
  items: IncompleteRecord[];
  total: number;
  by_entity_type: Partial<Record<CompletenessEntityType, number>>;
}

export interface FieldMissingReport {
  entity_type: CompletenessEntityType;
  entity_id: UUID;
  missing_fields: string[];
  explanations: string[];
}

// ─── Expiring dates (Phase E2) ────────────────────────────────────────────
export type ExpiringDateKind =
  | 'KYC' | 'AML' | 'MIFID_SUITABILITY' | 'ID_EXPIRY'
  // Beta #6b (migration 0021): Portuguese-law legal-entity registry docs.
  | 'PCC_EXPIRY' | 'RCBE_EXPIRY'
  // Beta #5 (migration 0023): LEI annual renewal.
  | 'LEI_EXPIRY'
  // P11 (2026-10-10): the spouse's ID document.
  | 'SPOUSE_ID_EXPIRY';

export interface ExpiringDateInfo {
  kind: ExpiringDateKind;
  expires_on: ISODate;
  /** Negative = overdue, 0 = today, positive = upcoming. */
  days_until_due: number;
  detail_path: string;
}

export interface ExpiringRecord {
  entity_type: CompletenessEntityType;
  entity_id: UUID;
  label: string;
  detail_path: string;
  dates: ExpiringDateInfo[];
}

export interface ExpiringReport {
  items: ExpiringRecord[];
  total: number;
  within_months: number;
  today: ISODate;
}

// P11 (2026-10-10): upcoming birthdays — every person, with their agreements + RMs.
export interface BirthdayAgreement {
  agreement_id: UUID;
  label: string;
  status: string;
  /** Named RMs, primary first. */
  rms: string[];
}

export interface UpcomingBirthday {
  person_id: UUID;
  name: string;
  next_birthday: ISODate;
  /** 0 = today. */
  days_until: number;
  turns: number;
  detail_path: string;
  agreements: BirthdayAgreement[];
}

export interface BirthdayReport {
  items: UpcomingBirthday[];
  within_days: number;
  today: ISODate;
}

// Feature 1 (#157, 2026-05-28): Agreement Interactions ("Notes" tab).

/** Migration 0035 (#41a0, 2026-06-12). EMAIL is manual today; the gated
 *  Outlook integration files EMAIL rows natively when it ships. */
export type InteractionKind = 'MEETING' | 'CALL' | 'EMAIL' | 'NOTE' | 'OTHER';

/** §3j friendly labels — single source for badges, selects, filters. */
export const INTERACTION_KIND_LABELS: Record<InteractionKind, string> = {
  MEETING: 'Meeting',
  CALL: 'Call',
  EMAIL: 'Email',
  NOTE: 'Note',
  OTHER: 'Other',
};

// ---------------------------------------------------------------------------
// §3j friendly-label maps (task #57, 2026-06-14). Badges across the app used
// to render raw enum tokens (HOLDER, JOINT_HOLDER, POLICY_HOLDER, PENDING,
// CREATE, OPEN ...). These maps are the single source of truth so every badge,
// select and filter shows the human form. Always apply as `MAP[x] ?? x` so an
// unmapped value degrades to the raw token rather than rendering blank.
// ---------------------------------------------------------------------------

/**
 * Party roles across all three contexts — agreement (PartyRole), account
 * (AccountPartyRole) and insurance-policy (InsurancePolicyPartyRole). The
 * token sets are disjoint and the one shared token (BENEFICIARY) carries the
 * same label in every context, so a single combined map serves every party
 * badge regardless of which role enum the row belongs to.
 */
export const PARTY_ROLE_LABELS: Record<string, string> = {
  // PartyRole (agreement)
  HOLDER: 'Holder',
  REPRESENTATIVE: 'Representative',
  BENEFICIARY: 'Beneficiary',
  // AccountPartyRole
  JOINT_HOLDER: 'Joint holder',
  INSURANCE_BENEFICIARY: 'Insurance beneficiary',
  // InsurancePolicyPartyRole
  POLICY_HOLDER: 'Policy holder',
  LIFE_INSURED: 'Life insured',
};

/** Change-request kinds (ChangeRequestKind). */
export const CHANGE_REQUEST_KIND_LABELS: Record<string, string> = {
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
  RESTORE: 'Restore',
};

/** Change-request statuses (ChangeRequestStatus). */
export const CHANGE_REQUEST_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  APPLIED: 'Applied',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

/** Agreement task statuses (AgreementTaskStatus). */
export const AGREEMENT_TASK_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

export interface AgreementInteraction {
  id: UUID;
  agreement_id: UUID;
  interaction_date: ISODate;     // user-picked: when the meeting/call happened
  kind: InteractionKind;         // 0035 (#41a0); pre-0035 rows backfilled NOTE
  title: string;
  body: string | null;
  is_private: boolean;
  has_other_attendees: boolean;

  created_at: ISODate;           // "filed at" — auto
  updated_at: ISODate;
  deleted_at: ISODate | null;
  created_by: UUID | null;
  updated_by: UUID | null;

  // Attendees + display labels (resolved by the router).
  person_ids: UUID[];
  user_ids: UUID[];
  person_labels: Record<string, string>;  // {person_id: display_name}
  user_labels: Record<string, string>;
  created_by_label: string | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AgreementInteractionCreateInput {
  agreement_id: UUID;
  interaction_date: ISODate;
  kind?: InteractionKind;        // backend defaults to NOTE when omitted
  title: string;
  body?: string | null;
  is_private?: boolean;
  has_other_attendees?: boolean;
  person_ids?: UUID[];
  user_ids?: UUID[];
}

export interface AgreementInteractionUpdateInput {
  interaction_date?: ISODate;
  kind?: InteractionKind;
  title?: string;
  body?: string | null;
  is_private?: boolean;
  has_other_attendees?: boolean;
  person_ids?: UUID[];
  user_ids?: UUID[];
}

/** #41 (2026-06-12): per-column filters on the Interactions list.
 *  Mirrors the backend query params on GET /agreement-interactions. */
export interface AgreementInteractionFilters {
  /** Multi-select (C-132 amendment 2026-06-12): repeated query keys. */
  kind?: InteractionKind[];
  date_from?: ISODate;
  date_to?: ISODate;
  q?: string;
  person_id?: UUID;
  user_id?: UUID;
  author_id?: UUID;
}

// (Duplicate block from earlier mount-race recovery removed — the canonical
// AgreementInteraction definitions live above.)

// Feature 4 (2026-05-28): Agreement Tasks.
export type AgreementTaskStatus = 'OPEN' | 'DONE' | 'CANCELLED';

export interface AgreementTask {
  id: UUID;
  /** null => a "General" task, related to no individual Agreement (#63). */
  agreement_id: UUID | null;
  description: string;
  deadline: ISODate;
  /** P14: 1 High · 2 Medium · 3 Low (utils/taskUrgency). */
  urgency: number;
  assignee_user_id: UUID;
  status: AgreementTaskStatus;
  status_changed_at: ISODateTime | null;
  status_changed_by: UUID | null;

  created_at: ISODateTime;
  updated_at: ISODateTime;
  deleted_at: ISODateTime | null;
  created_by: UUID | null;
  updated_by: UUID | null;

  // Display labels resolved server-side.
  assignee_label: string | null;
  created_by_label: string | null;
  status_changed_by_label: string | null;
  /** P42: the related agreement's Primary Named RM(s), joined; null for General / none. */
  rm_label?: string | null;
  /** The related agreement's label from the server (deleted ones marked "(deleted)"). */
  agreement_label?: string | null;
  /** Task 4 (2026-06-09): pending CR refs against this row. Empty when none. */
  pending_change_requests: PendingChangeRequestRef[];
}

export interface AgreementTaskCreateInput {
  /** null => a "General" task (no Agreement). #63. */
  agreement_id: UUID | null;
  description: string;
  deadline: ISODate;
  urgency?: number;
  assignee_user_id: UUID;
}

export interface AgreementTaskUpdateInput {
  description?: string;
  deadline?: ISODate;
  urgency?: number;
  assignee_user_id?: UUID;
}
